import { PropsWithChildren, ReactNode } from "react";
import { TableContainer, Paper, TableHead, TableRow, TableCell, TableBody, Table, styled, CircularProgress } from "@mui/material";
import StyledPagination from "./styled-pagination";

export type ColumnHeader = {
    name: string;
    className?: string;
};

interface TableProps {
    readonly headers: ColumnHeader[],
    readonly currentPage: number,
    readonly totalPages: number,
    readonly loading?: boolean,
    readonly emptyMessage?: string,
    readonly onPageChange: (page: number) => void
}

const StyledHeaderRow = styled(TableRow)(({ theme }) => ({
    '& .MuiTableCell-root': {
        background: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.secondary.light} !important`
    }
}));

const StyledTBody = styled(TableBody)(({ theme }) => ({
    '& .MuiTableRow-root': {
        [theme.breakpoints.down('sm')]: {
            paddingBottom: '16px'
        }
    },
    '& .MuiTableCell-root': {
        paddingTop: '8px'
    },
    '& .MuiTableCell-root .btn': {
        padding: '0 1.6rem 0 0',
    },
    '& .responsive-mobile-heading': {
        display: 'inline-block',
        width: '40%',
        color: `${theme.palette.secondary.dark}`,
        fontWeight: 'bold',
        [theme.breakpoints.up('md')]: {
            display: 'none'
        }
    },
    '& .table-action-button': {
    }
}));

const StyledTable = ({ headers, currentPage, loading, totalPages, onPageChange, emptyMessage = "No records available.", children }: PropsWithChildren<TableProps>) => {
    let hasRows = Boolean(children);

    if (Array.isArray(children)) {
        hasRows = children.length > 0;
    }

    const colSpan = headers.length;
    let bodyContent: ReactNode;

    if (loading) {
        bodyContent = (
            <TableRow>
                <TableCell colSpan={colSpan} align="center">
                    <CircularProgress aria-label="Loading…" />
                </TableCell>
            </TableRow>
        );
    } else if (!hasRows) {
        bodyContent = (
            <TableRow>
                <TableCell colSpan={colSpan} align="center">
                    {emptyMessage}
                </TableCell>
            </TableRow>
        );
    } else {
        bodyContent = children;
    }

    return (
        <div className="table-responsive mt-3 p-0">
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <StyledHeaderRow>
                            {headers.map(header => <TableCell key={header.name} className={header.className}>{header.name}</TableCell>)}
                        </StyledHeaderRow>
                    </TableHead>
                    <StyledTBody>{bodyContent}</StyledTBody>
                </Table>
            </TableContainer>
            <StyledPagination currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
            />
        </div>
    );
}

export default StyledTable;